import React from 'react';
import { useTranslation } from 'react-i18next';
import { fmtNum, fmtHM, fmtPct } from '../../lib/formatHelpers';

/**
 * V2 HeroProjection — Hero immersif pour manager : "ce que mon équipe a fait
 * ce mois-ci jusqu'à présent", avec un breakdown des contributions.
 *
 * NoiRock adaptation Phase 1 :
 *  - Le Black OS Hero affichait des USD (commission, bonus, projection).
 *    On n'a pas encore l'Edge Function generic-decrypt côté Generic, donc on
 *    affiche les métriques cleartext disponibles : créateurs actifs / sessions /
 *    heures / jours valides cumulés. Les chiffres financiers viendront Sprint 5.
 *
 * Props :
 *  - activeCreators / totalCreators : numérateur + dénominateur équipe
 *  - sessions / hoursTotal / validGoLiveDays : aggregate MTD
 *  - deltaPct : (optionnel) variation vs M-1 (ratio simple jours-écoulés)
 *  - loading
 */
const Stat = ({ label, value, sub }) => (
  <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold">{label}</div>
    <div className="text-2xl md:text-3xl font-heading font-bold text-foreground tabular-nums mt-1 truncate">{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{sub}</div>}
  </div>
);

const HeroProjection = ({
  activeCreators = 0,
  totalCreators = 0,
  sessions = 0,
  hoursTotal = 0,
  validGoLiveDays = 0,
  deltaPct = null,
  loading = false,
}) => {
  const { t } = useTranslation();
  const activeRatio = totalCreators > 0 ? (activeCreators / totalCreators) * 100 : null;

  return (
    <section className="rounded-2xl bg-card border border-border overflow-hidden">
      <div className="px-5 md:px-6 py-4 border-b border-border bg-surface-2 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 font-semibold">
            {t('managerV2.heroProjection.label', 'Team performance')}
          </div>
          <div className="text-sm font-heading text-foreground mt-0.5">
            {t('managerV2.heroProjection.thisMonth', 'This month so far')}
          </div>
        </div>
        {deltaPct != null && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold">
              {t('managerV2.heroProjection.vsLastMonth', 'vs last month')}
            </div>
            <div className={`text-lg font-heading font-bold tabular-nums mt-0.5 ${
              deltaPct >= 0 ? 'text-success' : 'text-error'
            }`}>
              {deltaPct >= 0 ? '↑' : '↓'} {fmtPct(Math.abs(deltaPct), 1)}
            </div>
          </div>
        )}
      </div>
      <div className="px-5 md:px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat
          label={t('managerV2.heroProjection.active', 'Active creators')}
          value={loading ? '…' : `${fmtNum(activeCreators)} / ${fmtNum(totalCreators)}`}
          sub={activeRatio != null ? fmtPct(activeRatio, 0) : null}
        />
        <Stat
          label={t('managerV2.heroProjection.sessions', 'Sessions')}
          value={loading ? '…' : fmtNum(sessions)}
        />
        <Stat
          label={t('managerV2.heroProjection.hours', 'Live hours')}
          value={loading ? '…' : fmtHM(hoursTotal * 3600)}
        />
        <Stat
          label={t('managerV2.heroProjection.validDays', 'Valid go-live days')}
          value={loading ? '…' : fmtNum(validGoLiveDays)}
        />
      </div>
    </section>
  );
};

export default HeroProjection;
